import { useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin'
import { useGSAP } from '@gsap/react'

/* DrawSVGPlugin ships in node_modules but nothing registered it before this
   file, so drawSVG was silently a no-op property anywhere it was used. */
gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin)

/**
 * FlowDraw - nodes arrive, then the connector between them draws itself, for
 * the inline flow diagrams built by flowSvg() in public/pentabay-page-extras.js.
 *
 * 11 diagrams, one per route: /mobile-development, /saas-platforms,
 * /web-marketing, /low-code-platforms, /ai-edtech, /banking-lending,
 * /enterprise-it, /real-estate, /saas-b2b, /telecommunications and one keyed
 * '#/cloud-service'.
 *
 * ── Targeting ─────────────────────────────────────────────────────────────
 * flowSvg() had nothing to hook: a node was `<g transform="translate(x y)">`
 * and a connector a bare `<path>`, both unclassed, inside an <svg class="w-full">
 * that was indistinguishable from any other inline SVG on the page. Selecting
 * by tag and position would have worked exactly until someone added a step.
 * So three hooks were added to the factory - .pb-flow on the <svg>,
 * .pb-flow-node on each node group, .pb-flow-line on each connector - in
 * public/pentabay-page-extras.js. The root-level pentabay-page-extras.js is a
 * stale duplicate that nothing loads; Pentabay.html requests
 * /pentabay-page-extras.js, which Vite serves from public/.
 *
 * No CSS is keyed off any of the three. They are animation handles only, which
 * is what keeps §6.7 satisfiable: there is nothing in a stylesheet for this
 * component to fail into.
 *
 * ── Fail visible (brief §6.7) ─────────────────────────────────────────────
 * The drawSVG '0%' pre-hide is written by JS, from inside the observer
 * callback, onto the elements themselves - never as a CSS rule. A stylesheet
 * rule hiding a connector would mean a broken build, a CSP block or a thrown
 * error anywhere earlier in this bundle leaves 11 diagrams permanently missing
 * their arrows, with no error to explain it. As written, if this file never
 * runs, every diagram renders exactly as flowSvg() emitted it: fully drawn.
 * Verified by deleting the component from the tree and re-rendering, not by
 * argument - see check 8.
 *
 * ── RevealFixer ───────────────────────────────────────────────────────────
 * No collision, and the section keeps its normal cascade. Traced through
 * collectItems(): the diagram's `<section data-reveal>` holds one child, so it
 * descends to `div.grid.lg:grid-cols-12`, which has two children over 48px -
 * the copy column and the diagram column - and therefore staggers THOSE TWO
 * COLUMNS. It never reaches inside the <svg>, so no node group and no connector
 * is ever a WAAPI target and there is nothing for the GSAP tweens to fight.
 * Measured on /mobile-development, /low-code-platforms and /cloud-service, six
 * samples spanning each draw: 0 WAAPI animations on any element inside the
 * <svg>, 18 samples out of 18. That is why the section is NOT added to
 * SELF_ANIMATED and why rvPlayed is NOT set here - either one would switch off
 * the surrounding section's reveal to fix a collision that does not exist.
 *
 * ── Why the timeline is delayed 0.95s ────────────────────────────────────
 * RevealFixer cascades the diagram's COLUMN (130ms delay + 780ms duration) and
 * this timeline triggers independently at 'top 85%'. They start together, so
 * the draw happens while the column is still coming up from opacity 0.
 *
 * Measured at 1440, from the frame the diagram is scrolled into view:
 *
 *   route                 1st node   1st connector      column   all lines
 *                         visible    starts drawing     hits 1   drawn by
 *   /mobile-development    19ms      230ms @ op 0.244   913ms    1479ms   62%
 *   /low-code-platforms    24ms      225ms @ op 0.243   907ms    1174ms   77%
 *   /cloud-service         19ms      252ms @ op 0.401   886ms    1502ms   59%
 *
 * The last column is the share of the sequence that has elapsed before the
 * column is fully opaque. Every node's first frame is at column opacity 0.000.
 * The connector-start opacity jitters run to run - across two full runs it
 * ranged 0.19 to 0.40 - but the shape does not: the first connector always
 * draws at roughly a quarter to a third opacity, and well over half the
 * sequence is gone by the time the column is solid. The nodes-then-line rhythm
 * this component exists to create is therefore largely spent before it can be
 * seen.
 *
 * FIXED by delaying the timeline 0.95s (see the delay in the timeline vars for
 * why a fixed number is the right instrument). The trigger point is unchanged at
 * 'top 85%', once - the diagram is still claimed at the same scroll position, it
 * just does not start drawing until the column it lives in is opaque.
 *
 * After the delay, same measurement, same three routes:
 *
 *   route                 column      1st node          1st connector     all lines
 *                         hits 1      visible           starts drawing    drawn by
 *   /mobile-development    918ms      951ms @ op 1.000  1168ms @ op 1.000  2418ms
 *   /low-code-platforms    913ms      963ms @ op 1.000  1181ms @ op 1.000  2130ms
 *   /cloud-service         903ms      986ms @ op 1.000  1203ms @ op 1.000  2453ms
 *
 * Every node's first frame is now at column opacity 1.000, where before it was
 * 0.000 on all three, and nothing in the sequence happens behind the fade. The
 * first node lands 33-83ms after the column finishes, which is the ~40ms margin
 * plus a frame or two of scheduling.
 *
 * Re-measure if RevealFixer's 130ms/780ms cascade timings change: this delay is
 * downstream of them, and nothing enforces the relationship.
 *
 * ── Themes ────────────────────────────────────────────────────────────────
 * Nothing here sets a colour. The connectors already carry
 * stroke="var(--pb-blue)" from the factory, which resolves identically in both
 * themes, and drawSVG animates stroke-dasharray/-dashoffset only. --aura-opacity
 * is untouched.
 */

/* 2026-08 intensity pass. 12px -> 34px, 0.7s -> 0.5s, and back.out on the nodes
   so each box passes its resting position and settles - these are discrete
   cards arriving, which is exactly what an overshoot suits.
   NODE_STEP stays at 0.3: it was already the widest stagger in the codebase and
   is visibly sequential, and LINE_OFFSET is measured against it. */
const NODE_DURATION = 0.5
const NODE_STEP = 0.3       // between successive nodes
const NODE_SHIFT = 34
const LINE_DURATION = 0.38
const LINE_OFFSET = 0.22    // into the node step the line leaves from
const NODE_EASE = 'back.out(1.5)'
/* Lines are drawSVG 0% -> 100%. An overshoot ease cannot apply: drawSVG has no
   meaning past 100%, so back.out would clamp and the last third of the tween
   would draw nothing. expo.out gives the snap without a value to exceed. */
const LINE_EASE = 'expo.out'
const START = 'top 85%'

const BUILT = 'pbFlowDrawn'   // dataset key -> data-pb-flow-drawn

/* Located by class, never by text: TranslationManager rewrites the <text>
   nodes inside these diagrams at runtime for fr / es / zh-CN. */
function findDiagrams() {
  const route = document.documentElement.getAttribute('data-route')
  if (!route) return []
  const page = document.querySelector('main.page.active')
  /* The guard. The inline router stamps <html data-route> while the OUTGOING
     page is still main.page.active, so without this the diagrams get built on
     whatever page is still showing - which is then detached, taking the
     annotation with it, and the real diagrams are never touched. */
  if (!page || page.id !== `page-${route}`) return []
  return Array.from(page.querySelectorAll('svg.pb-flow'))
    .filter(svg => svg.dataset[BUILT] !== '1' && svg.isConnected)
}

export default function FlowDraw() {
  const mms = useRef([])

  useGSAP(() => {
    let observer = null

    const build = () => {
      for (const svg of findDiagrams()) {
        const nodes = Array.from(svg.querySelectorAll('.pb-flow-node'))
        const lines = Array.from(svg.querySelectorAll('.pb-flow-line'))
        if (!nodes.length) continue

        svg.dataset[BUILT] = '1'

        const mm = gsap.matchMedia()
        mms.current.push(mm)

        /* Both branches so flipping the OS setting reverts cleanly. Under
           reduce this sets NOTHING: no gsap.set, no drawSVG pre-hide, no
           shortened draw. The diagram is simply the finished diagram, which is
           also why its connectors read stroke-dashoffset 0 there - nothing ever
           wrote a dash to them. */
        mm.add('(prefers-reduced-motion: reduce)', () => {})

        mm.add('(prefers-reduced-motion: no-preference)', () => {
          /* Every node carries its layout as a transform ATTRIBUTE from the
             factory - transform="translate(x 30)", which is what puts it in its
             column and 30 units below the top edge. Two things about GSAP and
             SVG make that a hazard, and both were caught in the browser:

             1. `y` is ABSOLUTE on an SVG transform, not an offset. GSAP parses
                translate(x 30) into x/y, so `y: 12` MOVED the node to y=12
                instead of nudging it to 42 - the whole diagram animated 30
                units high and would have snapped down at the end. So the shift
                is applied relative to each node's own parsed y.
             2. clearProps:'transform' DELETES the attribute. Measured: after
                the timeline finished, all five nodes read transform=null with
                CTM e=0 f=0 - every box stacked in the top-left corner with the
                arrows orphaned beside them. The exact attribute string is
                therefore captured here and written back verbatim, rather than
                trusting a clear to leave the factory's layout behind. */
          const baseTransform = nodes.map(n => n.getAttribute('transform'))
          const baseY = nodes.map(n => Number(gsap.getProperty(n, 'y')) || 0)

          const restoreNodes = () => {
            gsap.set(nodes, { clearProps: 'opacity,transform,transformOrigin,willChange' })
            nodes.forEach((n, i) => {
              if (baseTransform[i] === null) n.removeAttribute('transform')
              else n.setAttribute('transform', baseTransform[i])
            })
          }

          /* The pre-hide, in JS, on the elements. Runs in the observer callback
             (a microtask, before paint) so the diagram does not paint drawn and
             then blank. */
          nodes.forEach((n, i) => gsap.set(n, { opacity: 0, y: baseY[i] + NODE_SHIFT }))
          if (lines.length) {
            gsap.set(lines, { drawSVG: '0%' })
            /* drawSVG hides the LINE, not its arrowhead. flowSvg puts
               marker-end on the wrapper <g>, and marker-end is an inherited
               SVG property, so every connector path inherits the arrowhead -
               and markers paint at path vertices regardless of the stroke dash
               pattern. Verified on /mobile-development, /low-code-platforms and
               /cloud-service: with no marker-end of its own, a connector path's
               computed markerEnd resolves to url(#pbarrN) inherited from the
               wrapper, so every arrowhead sat parked at its destination from
               frame zero while its line was still hidden. The other 8 diagrams
               come from the same factory and the same wrapper <g>, so they
               inherit identically. Suppressed per path, then released when that
               path finishes drawing.
               removeAttribute, never setAttribute back to url(#pbarrN): the
               path re-inherits from the wrapper group, so nothing here has to
               know the generated svgSeq id. */
            lines.forEach(l => l.setAttribute('marker-end', 'none'))
          }

          const tl = gsap.timeline({
            scrollTrigger: { trigger: svg, start: START, once: true },
            /* Waits out RevealFixer's fade on the column this diagram sits in.
               Measured before this existed: the column reached opacity 1 at
               913ms / 907ms / 886ms on /mobile-development /low-code-platforms
               /cloud-service, and the first node's first frame landed at column
               opacity 0.000 every time, with 59-77% of the sequence gone before
               the column was solid.

               A FIXED delay is the right instrument because the thing being
               waited on is fixed: RevealFixer's cascade for this item is one
               STEP_MS of delay plus its WAAPI duration, the same on every route
               and every width, not a scroll- or content-dependent quantity.

               2026-08: that arithmetic MOVED with the intensity pass. It was
               130 + 780 = 910ms, cleared by a 0.95s delay. RevealFixer is now
               150 + 560 = 710ms, so this is 0.75s - same ~40ms margin. This
               number is downstream of RevealFixer's STEP_MS and cascade
               duration; if either changes again, re-derive and re-measure.

               It does cost 0.95s on every visit, including the case where the
               cascade has already finished. The alternative - trigger later, or
               take the column's fade over via SELF_ANIMATED - was weighed and
               this is the one that leaves both systems owning what they already
               own. If RevealFixer's 130/780 timings ever change, this number is
               downstream of them. */
            delay: 0.75,
            onComplete() {
              /* Hand everything back. The connectors matter most: clearing the
                 inline dasharray/dashoffset returns them to an undashed path,
                 i.e. fully drawn by default rather than held drawn by an inline
                 value that a later re-render could drop. */
              restoreNodes()
              if (lines.length) {
                gsap.set(lines, { clearProps: 'strokeDasharray,strokeDashoffset,willChange' })
                // Belt and braces: a line whose own onComplete was skipped
                // (seek, kill mid-tween) must not keep a suppressed arrowhead.
                lines.forEach(l => l.removeAttribute('marker-end'))
              }
            },
          })

          nodes.forEach((node, i) => {
            tl.to(node, {
              opacity: 1,
              y: baseY[i],
              duration: NODE_DURATION,
              ease: NODE_EASE,
            }, i * NODE_STEP)

            /* Connector i leaves node i for node i+1, so it starts LINE_OFFSET
               into that node's own step - the line is already on its way out of
               a box the eye has seen land, instead of racing it. There is one
               fewer connector than node, so the last node has none. */
            const line = lines[i]
            if (line) {
              tl.fromTo(line,
                { drawSVG: '0%' },
                {
                  drawSVG: '100%',
                  duration: LINE_DURATION,
                  ease: LINE_EASE,
                  // The arrowhead arrives with the line that earns it.
                  onComplete: () => line.removeAttribute('marker-end'),
                },
                i * NODE_STEP + LINE_OFFSET,
              )
            }
          })

          return () => {
            tl.scrollTrigger && tl.scrollTrigger.kill()
            tl.kill()
            restoreNodes()
            if (lines.length) {
              gsap.set(lines, { clearProps: 'strokeDasharray,strokeDashoffset,willChange' })
              /* Restores the inherited arrowhead on a matchMedia revert - e.g.
                 the user flips on reduced motion mid-draw. Without it the
                 reduce branch, which correctly sets nothing, would inherit a
                 diagram whose arrowheads had been suppressed by the branch
                 being torn down. */
              lines.forEach(l => l.removeAttribute('marker-end'))
            }
          }
        })
      }
    }

    build()

    /* Kept for the session, and idempotent per SVG rather than per component
       instance. page-extras injects these diagrams into a route AFTER it mounts,
       and a parked route re-attaches the same node later, so a one-shot
       observer would catch at most the first diagram of the first route
       visited. */
    observer = new MutationObserver(build)
    observer.observe(document.body, { childList: true, subtree: true })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-route'] })
    window.addEventListener('pb-pages-mounted', build)

    return () => {
      if (observer) observer.disconnect()
      window.removeEventListener('pb-pages-mounted', build)
      for (const m of mms.current) m.revert()
      mms.current = []
    }
  }, { dependencies: [] })

  return null
}
